// apps/app/src/components/layout/sidebar.tsx
//
// The rail: a dark floating panel beside the content. Collapses to icons (the
// choice is remembered), and below 1024px the same navigation opens in a drawer
// from the top bar — previously there was no navigation at all on a tablet.

import { NavLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PanelLeftClose, PanelLeftOpen, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { http } from '@/lib/api-client';
import { keys } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { can } from '@/lib/permissions';
import { useUi } from '@/lib/ui-store';
import { cn } from '@/lib/utils';
import { AccountMenu } from './account-menu';
import { CLINIC_GROUPS, PLATFORM_GROUPS, type BadgeKey, type NavItem } from './nav-config';

interface OnboardingState {
  percent: number;
  completed: number;
  total: number;
  canGoLive: boolean;
}

/** The desktop rail. Hidden below lg, where MobileNav takes over. */
export function Sidebar() {
  const collapsed = useUi((s) => s.sidebarCollapsed);
  const toggle = useUi((s) => s.toggleSidebar);
  const { t } = useTranslation('common');

  return (
    <aside
      className={cn(
        'group/rail hidden shrink-0 flex-col overflow-hidden rounded-xl bg-sidebar text-sidebar-foreground transition-[width] duration-base ease-enter lg:flex',
        collapsed ? 'w-[60px]' : 'w-60',
      )}
    >
      {/* Fixed height, so swapping the wordmark for the toggle never shifts the nav. */}
      <div className={cn('flex h-14 shrink-0 items-center px-3', collapsed ? 'justify-center' : 'justify-between pl-4')}>
        {collapsed ? (
          <span className="grid size-9 place-items-center text-sm font-bold text-white group-hover/rail:hidden">SV</span>
        ) : (
          <span className="text-[15px] font-semibold tracking-tight text-white">SkinVizo</span>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
              className={cn(
                'text-sidebar-muted hover:bg-sidebar-accent hover:text-white',
                collapsed ? 'hidden group-hover/rail:inline-flex' : 'size-8',
              )}
            >
              {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}</TooltipContent>
        </Tooltip>
      </div>

      <div className="shrink-0 px-2 pb-3">
        <AccountMenu collapsed={collapsed} />
      </div>

      <SidebarNav collapsed={collapsed} />
    </aside>
  );
}

/**
 * The navigation itself, shared by the rail and the mobile drawer.
 * `onNavigate` lets the drawer close itself when a link is chosen.
 */
export function SidebarNav({ collapsed = false, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const user = useAuth((s) => s.user);
  const isPlatform = can(user?.role, 'platform.manage');
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  // Only roles that can complete setup are nudged towards it.
  const { data: onboarding } = useQuery({
    queryKey: keys.onboarding,
    queryFn: () => http.get<OnboardingState>('/tenants/me/onboarding'),
    enabled: Boolean(user?.tenantId) && can(user?.role, 'clinic.manage'),
  });

  const { data: counts } = useQuery({
    queryKey: ['sidebar-counts'],
    queryFn: async () => {
      const [leads, requests] = await Promise.all([
        http.get<{ status: string; count: number }[]>('/leads/pipeline').catch(() => []),
        http.get<unknown[]>('/booking/requests?status=NEW').catch(() => []),
      ]);
      return {
        leads: leads.find((l) => l.status === 'NEW')?.count ?? 0,
        requests: requests.length,
      };
    },
    // Tenant-scoped: platform staff belong to no clinic, so these would only
    // 403 on every page load.
    enabled: Boolean(user?.tenantId),
    refetchInterval: 60_000,
  });

  // Shares the cache with the overview page and the settings nav.
  const { data: overview } = useQuery({
    queryKey: keys.platformOverview,
    queryFn: () => http.get<{ pendingVerification: number }>('/platform/overview'),
    enabled: isPlatform,
    refetchInterval: 60_000,
  });

  const badges: Record<BadgeKey, number> = {
    leads: counts?.leads ?? 0,
    requests: counts?.requests ?? 0,
    verification: overview?.pendingVerification ?? 0,
  };

  // Within staleTime this is a no-op, so sweeping the pointer down the nav
  // does not fire a request per item.
  const prefetch = (item: NavItem) => {
    if (!item.prefetch) return;
    const { queryKey, path } = item.prefetch;
    void queryClient.prefetchQuery({ queryKey, queryFn: () => http.get(path) });
  };

  const groups = isPlatform ? PLATFORM_GROUPS : CLINIC_GROUPS;
  const incomplete = onboarding && onboarding.percent < 100;

  return (
    <>
      <nav className="flex-1 space-y-5 overflow-y-auto px-2 pb-3" aria-label={t('nav.label')}>
        {groups.map((group) => {
          const visible = group.items.filter((i) => {
            if (i.capability && !can(user?.role, i.capability)) return false;
            // Every clinic item reads tenant-scoped data, so without a clinic
            // each one can only 403.
            if (!isPlatform && !user?.tenantId) return false;
            return true;
          });
          if (visible.length === 0) return null;

          return (
            <div key={group.titleKey}>
              {collapsed ? (
                <div className="mx-auto mb-2 h-px w-6 bg-sidebar-border" aria-hidden="true" />
              ) : (
                <p className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted">
                  {t(group.titleKey)}
                </p>
              )}

              <ul className="space-y-0.5">
                {visible.map((item) => (
                  <li key={item.to}>
                    <NavEntry
                      item={item}
                      collapsed={collapsed}
                      badge={item.badge ? badges[item.badge] : 0}
                      onHover={() => prefetch(item)}
                      {...(onNavigate ? { onNavigate } : {})}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Pinned until setup is done. Seeing the destination is motivating; a
          bare wizard feels like a form. */}
      {incomplete && !collapsed && (
        <NavLink
          to="/onboarding"
          onClick={onNavigate}
          className="mx-2 mb-2 rounded-lg bg-sidebar-accent p-3 transition-colors duration-fast hover:bg-sidebar-border"
        >
          <p className="flex items-center gap-1.5 text-xs font-medium text-white">
            <Rocket className="size-3.5" aria-hidden="true" />
            {t('nav.finishSetup')}
          </p>
          <div className="mt-2 h-1 overflow-hidden rounded-full bg-sidebar-border">
            <div
              className="h-full rounded-full bg-white transition-[width] duration-panel"
              style={{ width: `${onboarding.percent}%` }}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-sidebar-muted">
            {t('nav.setupSteps', { completed: onboarding.completed, total: onboarding.total })}
          </p>
        </NavLink>
      )}
    </>
  );
}

function NavEntry({
  item,
  collapsed,
  badge,
  onHover,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  badge: number;
  onHover: () => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('common');
  const Icon = item.icon;
  const label = t(item.labelKey);
  const link = (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      onMouseEnter={onHover}
      onFocus={onHover}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'relative flex items-center rounded-md text-sm transition-colors duration-fast',
          collapsed ? 'mx-auto size-9 justify-center' : 'gap-2.5 px-2.5 py-2',
          isActive
            ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-white',
        )
      }
    >
      <Icon className="size-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
      {collapsed ? (
        badge > 0 && <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-white" aria-label={t('nav.badgeNew', { count: badge })} />
      ) : (
        <>
          <span className="flex-1 truncate">{label}</span>
          {badge > 0 && (
            <span className="rounded-full bg-sidebar-border px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums">
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;

  // The trigger is a wrapper, not the link: Radix's Slot merges className as a
  // string, and NavLink's className is a function — it would be stringified and
  // the collapsed link would lose every style, active state included.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex justify-center">{link}</div>
      </TooltipTrigger>
      <TooltipContent side="right">
        {label}
        {badge > 0 ? ` · ${badge}` : ''}
      </TooltipContent>
    </Tooltip>
  );
}
